import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { CreateWorkspaceDialog } from '@/modules/workspaces';

import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Flex,
  Icon,
  SidebarMenuButton,
  SidebarMenuItem,
  Skeleton,
  Text,
} from '@kognitos/lattice';
import {
  currentUserOrganizationWorkspaces,
  getCurrentUserOrganizationWorkspacesQueryKey,
  useGetWorkspace,
} from '@/generated/hooks/agora/v1/workspace';
import {
  useUserPreferencesStore,
  selectCurrentWorkspaceId,
  selectCurrentOrganizationId,
} from '@/shared/stores';
import WorkspaceIdentifier from '@/shared/components/workspace/WorkspaceIdentifier';
import { getRoutePath } from '@/routes/util';
import type {
  WorkspaceColorType,
  WorkspaceIconType,
} from '@/modules/onboarding/constants';
import { parseResourceName } from '@/shared/utils';

const WORKSPACES_PAGE_SIZE = 20;

const WorkspaceSwitcher = ({
  isCollapsed,
  onDropdownOpenChange,
}: {
  isCollapsed: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}) => {
  const { t: tNav } = useTranslation('navigation');
  const {
    value: isOpen,
    setTrue: setMenuOpen,
    setFalse: setMenuClose,
  } = useBoolean(false);
  const navigate = useNavigate();
  const {
    value: isCreateWorkspaceDialogOpen,
    setTrue: openCreateWorkspaceDialog,
    setFalse: closeCreateWorkspaceDialog,
  } = useBoolean();
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );

  // Dropdown-only data: fetched on open, never on mount. A failure here stays
  // in the dropdown — the trigger's label comes from useGetWorkspace below.
  const {
    data: workspacesData,
    isLoading: isWorkspacesLoading,
    isError: isWorkspacesError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: getCurrentUserOrganizationWorkspacesQueryKey(
      currentOrganizationId,
      { page_size: WORKSPACES_PAGE_SIZE },
    ),
    queryFn: ({ pageParam }) =>
      currentUserOrganizationWorkspaces(currentOrganizationId, {
        page_size: WORKSPACES_PAGE_SIZE,
        page_token: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage?.data?.next_page_token || undefined,
    enabled: !!currentOrganizationId && isOpen,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Flatten the paginated workspaces into a single list.
  const userWorkspaces = useMemo(
    () =>
      workspacesData?.pages.flatMap(page => page.data.user_workspaces ?? []) ??
      [],
    [workspacesData],
  );

  // Fetch the current workspace directly rather than searching the paginated
  // list, which may not contain it once results span multiple pages.
  const {
    data: currentWorkspaceData,
    isLoading: isCurrentWorkspaceLoading,
    isError: isCurrentWorkspaceError,
  } = useGetWorkspace(currentOrganizationId, currentWorkspaceId);
  const currentWorkspace = currentWorkspaceData?.data ?? null;
  const switcherLabel = isCurrentWorkspaceError
    ? tNav('navigation.workspaceSwitcher.error.title')
    : currentWorkspace?.title || tNav('navigation.workspaceSwitcher.workspace');

  // Load the next page when the user scrolls near the bottom of the list.
  const handleWorkspaceListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      scrollHeight - scrollTop - clientHeight < 48
    ) {
      fetchNextPage();
    }
  };

  const handleWorkspaceSelect = (newWorkspaceId: string) => {
    if (!currentOrganizationId || !newWorkspaceId) return;
    // Navigate to the workspace root so ProtectedRoute picks landing
    // page based on whether the target workspace has any published automations.
    navigate(
      getRoutePath('WORKSPACE', {
        orgId: currentOrganizationId,
        workspaceId: newWorkspaceId,
      }),
    );
  };

  // Map workspace data to display format with shortcuts
  const workspacesWithShortcuts = useMemo(() => {
    return userWorkspaces.map((workspace, index) => ({
      ...workspace,
      shortcut: `mod+alt+${index + 1}`,
      // Use default values for icon and color
      displayIcon: workspace.workspace_icon as WorkspaceIconType,
      displayColor: workspace.workspace_icon_color as WorkspaceColorType,
    }));
  }, [userWorkspaces]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setMenuOpen();
    } else {
      setMenuClose();
    }
    onDropdownOpenChange?.(open);
  };

  // Render dropdown content based on state
  const renderWorkspaceSwitcherContent = () => {
    // Loading state
    if (isWorkspacesLoading) {
      return (
        <>
          <div className="max-h-[320px] overflow-y-auto">
            {['1', '2', '3'].map(skeletonId => (
              <DropdownMenuItem
                key={`workspace-switcher-skeleton-${skeletonId}`}
              >
                <Skeleton className="h-6 w-6 bg-accent" />
                <Skeleton className="h-4 w-36 bg-accent" />
              </DropdownMenuItem>
            ))}
          </div>
        </>
      );
    }

    // Error state
    if (isWorkspacesError) {
      return (
        <Flex direction="column" align="center" gap={1} padding={6}>
          <Text level="small" color="destructive" weight="normal">
            {tNav('navigation.workspaceSwitcher.error.title')}
          </Text>
          <Text level="xSmall" color="muted" align="center">
            {tNav('navigation.workspaceSwitcher.error.description')}
          </Text>
        </Flex>
      );
    }

    // Success state
    return (
      <>
        <div
          className="max-h-[320px] overflow-y-auto"
          onScroll={handleWorkspaceListScroll}
          data-testid="workspace-switcher-list"
        >
          {workspacesWithShortcuts?.length > 0 ? (
            workspacesWithShortcuts.map(workspace => {
              const workspaceId = parseResourceName(workspace.name!).workspace;
              if (!workspaceId) return null;

              const handleClick = () => {
                handleOpenChange(false);
                handleWorkspaceSelect(workspaceId);
              };

              const isActive = workspaceId === currentWorkspaceId;

              return (
                <DropdownMenuItem
                  key={workspace.name}
                  onClick={handleClick}
                  className={cn('flex items-center gap-3 px-3 py-2', {
                    'bg-accent/50': isActive,
                  })}
                  data-testid={`workspace-switcher-item-${workspace.title}`}
                >
                  <WorkspaceIdentifier
                    color={workspace.displayColor as WorkspaceColorType}
                    icon={workspace.displayIcon}
                  />
                  <span className="flex-1 text-sm">{workspace.title}</span>
                </DropdownMenuItem>
              );
            })
          ) : (
            <DropdownMenuItem
              disabled
              className="flex items-center gap-3 px-3 py-2"
            >
              <span className="flex-1 text-sm text-muted-foreground">
                {tNav('navigation.workspaceSwitcher.noWorkspaces')}
              </span>
            </DropdownMenuItem>
          )}
          {isFetchingNextPage && (
            <DropdownMenuItem disabled className="flex items-center gap-3">
              <Skeleton className="h-6 w-6 bg-accent" />
              <Skeleton className="h-4 w-36 bg-accent" />
            </DropdownMenuItem>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <SidebarMenuItem>
        <DropdownMenu
          open={isOpen}
          onOpenChange={handleOpenChange}
          modal={false}
        >
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="default"
              variant="outline"
              data-testid="workspace-switcher-trigger"
              aria-label={isCollapsed ? switcherLabel : undefined}
              className="app-sidebar-ws-trigger mb-1 shadow-xs"
            >
              <Flex
                align="center"
                justify="center"
                className="app-sidebar-row-icon w-10 shrink-0"
              >
                <WorkspaceIdentifier
                  isLoading={isCurrentWorkspaceLoading}
                  color={
                    currentWorkspace?.workspace_icon_color as WorkspaceColorType
                  }
                  icon={currentWorkspace?.workspace_icon as WorkspaceIconType}
                  className="h-[24px] w-[24px] rounded-full"
                />
              </Flex>
              {!isCollapsed && (
                <>
                  <span
                    className={cn(
                      'app-sidebar-row-label min-w-0 flex-1 truncate text-[13.5px] font-medium text-sidebar-foreground',
                      {
                        'text-destructive': isCurrentWorkspaceError,
                      },
                    )}
                  >
                    {isCurrentWorkspaceLoading ? (
                      <Skeleton className="h-4 w-36 bg-accent" />
                    ) : isCurrentWorkspaceError ? (
                      tNav('navigation.workspaceSwitcher.error.title')
                    ) : (
                      currentWorkspace?.title
                    )}
                  </span>
                  <Icon
                    type="ChevronsUpDown"
                    size={15}
                    className="mr-3 text-sidebar-muted"
                  />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width)"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-mono text-[10.5px] font-medium tracking-[0.1em] text-sidebar-muted uppercase">
              {tNav('navigation.workspaceSwitcher.workspace')}
            </DropdownMenuLabel>

            {renderWorkspaceSwitcherContent()}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => {
                handleOpenChange(false);
                openCreateWorkspaceDialog();
              }}
              className="flex items-center gap-3 px-3 py-2"
            >
              <div className="h-5 w-5 rounded-lg bg-muted flex items-center justify-center">
                <Icon type="Plus" size="xs" className="text-muted-foreground" />
              </div>
              <span className="flex-1 text-sm font-medium">
                {tNav('navigation.workspaceSwitcher.createNew')}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <CreateWorkspaceDialog
        isOpen={isCreateWorkspaceDialogOpen}
        onClose={closeCreateWorkspaceDialog}
      />
    </>
  );
};

export default WorkspaceSwitcher;
